// src/pages/StudentLibrary.jsx — the student's saved tests & papers (/library).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HiClipboardList, HiDocumentText, HiTrash, HiArrowRight } from "react-icons/hi";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import { listSavedTests, listSavedPapers, removeSaved } from "../lib/bookmarks";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };

export default function StudentLibrary() {
  const { user } = useAuth();
  const [tab, setTab] = useState("tests");
  const [tests, setTests] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [t, p] = await Promise.all([listSavedTests(user.uid), listSavedPapers(user.uid)]);
        if (!active) return;
        setTests(t);
        setPapers(p);
      } catch (e) {
        console.error("Library load failed", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  const remove = async (kind, id) => {
    if (!user) return;
    if (kind === "test") setTests((xs) => xs.filter((x) => x.id !== id));
    else setPapers((xs) => xs.filter((x) => x.id !== id));
    try { await removeSaved(user.uid, kind === "test" ? "test" : "paper", id); } catch (e) { console.error(e); }
  };

  const items = tab === "tests" ? tests : papers;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="My Library" description="Your saved tests and study papers." canonical="/library" />

      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-800">📑 My Library</h1>
          <p className="text-sm text-gray-400 mt-0.5">Everything you saved to revisit later.</p>
        </div>
      </div>

      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 mb-5">
          <button
            onClick={() => setTab("tests")}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === "tests" ? "bg-indigo-600 text-white" : "text-gray-500"}`}
          >
            Saved Tests {tests.length > 0 && <span className="opacity-70">({tests.length})</span>}
          </button>
          <button
            onClick={() => setTab("papers")}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === "papers" ? "bg-indigo-600 text-white" : "text-gray-500"}`}
          >
            Saved Papers {papers.length > 0 && <span className="opacity-70">({papers.length})</span>}
          </button>
        </div>

        {loading ? (
          <div className="py-16"><Loader /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center">
            <span className="text-4xl block mb-3">{tab === "tests" ? "📝" : "📄"}</span>
            <p className="font-bold text-gray-600 mb-1">No saved {tab === "tests" ? "tests" : "papers"} yet</p>
            <p className="text-sm text-gray-400 mb-4">
              Tap the bookmark icon on any {tab === "tests" ? "quiz" : "study paper"} to save it here.
            </p>
            <Link
              to={tab === "tests" ? "/mcq" : "/practice"}
              className="inline-flex items-center gap-1 bg-indigo-600 text-white font-bold text-sm px-4 py-2 rounded-xl hover:bg-indigo-700 transition-colors"
            >
              {tab === "tests" ? "Browse tests" : "Browse materials"} <HiArrowRight />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((it, i) => {
              const to = tab === "tests" ? `/mcq/${it.mcqId}` : `/paper/${it.resourceId}`;
              const icon = tab === "tests" ? <HiClipboardList /> : <HiDocumentText />;
              const title = tab === "tests" ? (it.topic || "Quiz") : (it.title || "Paper");
              return (
                <motion.div
                  key={it.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 hover:border-indigo-200 hover:shadow-md transition-all shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-xl shrink-0">
                    {icon}
                  </div>
                  <Link to={to} className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-800 truncate">{title}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {it.board ? BOARD_LABELS[it.board] || it.board : it.type ? BOARD_LABELS[it.type] || it.type : ""}
                      {it.grade ? ` · Class ${it.grade}` : ""}
                      {it.subject ? ` · ${it.subject}` : ""}
                    </p>
                  </Link>
                  <button
                    onClick={() => remove(tab === "tests" ? "test" : "paper", it.id)}
                    className="shrink-0 text-gray-300 hover:text-red-500 p-1.5 transition-colors"
                    aria-label="Remove from Library"
                    title="Remove"
                  >
                    <HiTrash className="text-lg" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
